import * as d3 from "d3";
import colorsVariables from "@/utilities/colors-variables.js";
import { STATS_SURVEY_TYPES } from "../../enums/enums";
import store from "../../store";

export default class TotalExperienceManager {
  static height = 3.5;
  static svg;
  static xScale;
  static circle;
  static nodes;
  static node;
  static nodeText;
  static nodeArrowState;
  static groupName;

  static createChart(width, data, router, surveyType, selectedQuarter) {
    let dataForDraw;
    if (data) {
      let filterDataBasedOnQuarter = data.filter(
        (item) => item.quarter === `Q${selectedQuarter}`
      );

      dataForDraw = selectedQuarter ? filterDataBasedOnQuarter : data;
    }

    let className = `total-experience-chart-${surveyType}`;

    // check if svg already exist or not
    if (d3.select(`.${className} svg`).empty()) {
      this.svg = d3
        .select(`.${className}`)
        .append("svg")
        .attr("height", `${dataForDraw.length * 3.1 + this.height}rem`)
        .attr("width", width - 40);
      this.drawGradientScale(width, dataForDraw, className);
      // this.drawXAxis(width, surveyType, className);
    }

    d3.select(`.${className} svg`)
      .attr("height", `${dataForDraw.length * 3.1 + this.height}rem`)
      .attr("width", width - 40);

    d3.select(`.${className} svg .gradient-scale`).attr(
      "height",
      `${(dataForDraw.length - 1) * 3.21 + this.height}rem`
    );

    this.drawExperienceChart(dataForDraw, router, className, width, surveyType);
  }

  static drawGradientScale(width, data, className) {
    if (d3.select(`.${className} svg .gradient-scale`).empty()) {
      const svgDefs = this.svg.append("defs");

      const linearGradient = svgDefs
        .append("linearGradient")
        .attr("id", "linear-gradient");

      linearGradient
        .selectAll("stop")
        .data([
          { offset: "0%", color: colorsVariables.colormadny },
          { offset: "25%", color: colorsVariables.colorsleepy },
          { offset: "50%", color: colorsVariables.colornormaly },
          { offset: "75%", color: colorsVariables.colorfany },
          { offset: "100%", color: colorsVariables.colormaxi },
        ])
        .enter()
        .append("stop")
        .attr("offset", (d) => {
          return d.offset;
        })
        .attr("stop-color", (d) => {
          return d.color;
        });
      // Draw the rectangle and fill with gradient
      let scaleContainer = d3
        .select(`.${className} svg`)
        .append("g")
        .attr("class", `${className}-scaleContainer`)

        .style("transform", `translate(0, 2rem)`);
      scaleContainer
        .append("rect")
        .attr("width", width - 60)
        .attr("height", `${(data.length - 1) * 3.21 + this.height}rem`)
        .attr("class", "gradient-scale")
        .attr("ry", "14")
        .style("fill", "url(#linear-gradient)")
        .style("cursor", "pointer");
    }
  }

  static drawExperienceChart(data, router, className, width, surveyType) {
    let xScale = d3
      .scaleLinear()
      .domain([surveyType == "RED" ? -100 : -75, 150]) // input
      .range([25, width - 80]);

    // Call the x axis in a group tag
    d3.select(`.${className}-scaleContainer`)
      .append("g")
      .attr("class", "scale-axis scale-axis-hide-tick")
      .style("transform", `translate(0, -2rem)`)
      .call(
        d3
          .axisBottom(xScale)
          .tickValues([surveyType == "RED" ? -100 : -75, 150])
      ); // Create an axis component with d3.axisLeft

    d3.selectAll(`.${className} .node`).remove();
    this.nodes = d3
      .select(`.${className}-scaleContainer`)
      .append("g")
      .style("transform", `translate(0,1rem)`);

    // join data
    this.node = this.nodes
      .selectAll(`.${className} g`)
      .data(data)
      .enter()
      .append("g")
      .attr(
        "class",
        (d) =>
          `node select-group-event node-quarter-${
            d.quarter
          } select-${d.surveyGroupName?.replace(" ", "_")}`
      )
      .style("transform", (d, index) => {
        return `translate(${xScale(d.avgScore)}px, ${index * 3}rem)`;
      });

    this.node
      .on("click", async (_, data) => {
        if (
          data.statsSurveyType &&
          data.statsSurveyType === STATS_SURVEY_TYPES.YEARLY
        ) {
          router.push({
            name: "Summary",
            params: { id: data.surveyId },
          });
        } else if (
          data.statsSurveyType === STATS_SURVEY_TYPES.DERIVED_FROM_YEARLY
        ) {
          this.getRedIdFromYedId(data.surveyId, router);
        } else {
          router.push({
            name: "SurveyCoreQuestions",
            params: { id: data.surveyId },
          });
        }
      })
      .on("mouseover", function() {
        d3.selectAll(`.${className} .node`).style("opacity", 0.08);
        d3.select(this)
          .style("opacity", 1)
          .style("cursor", "pointer");
      })
      .on("mouseout", function() {
        d3.selectAll(`.${className} .node`).style("opacity", 1);
        d3.select(this)
          .style("opacity", 1)
          .style("cursor", "none");
      });

    let surveyInfo = this.node
      .append("g")
      .style("transform", `translate(1.5rem, 2rem)`);
    this.circle = this.node.append("circle");
    this.nodeText = surveyInfo.append("text");
    this.groupName = surveyInfo.append("text");
    this.nodeArrowState = this.node
      .append("g")
      .attr("class", "arrow-state")
      .style("display", (d) => {
        return d.growthDirection === null ? "none" : "block";
      });

    this.circle
      .attr("class", "circle")
      .attr("r", "1rem")
      .style("transform", `translate(0, 1rem)`)
      .style("fill", "white");

    this.nodeText
      .attr("text-anchor", "left")
      .style("fill", "#fff")
      .style("font-family", "Degular")
      .style("font-size", "1.5rem")
      .style("font-weight", "500")
      .attr("class", "text")
      .text((d) => Number(Math.round(d.avgScore)));

    this.groupName
      .attr("text-anchor", "left")
      .style("fill", "#fff")
      .style("width", "40px")
      .style("font-family", "Degular")
      .style("font-size", ".75rem")
      .style("font-weight", "600")
      .style("text-transform", "uppercase")
      .style("display", "block")
      .attr("class", `text group-name-text`)
      .style("transform", `translate(0, -1.5rem)`)
      .text((d) => d.surveyGroupName);

    this.nodeArrowState
      .append("circle")
      .attr("r", ".6875rem")
      .attr("class", "text")
      .style("fill", (d) => {
        return d.growthDirection === "UP"
          ? colorsVariables.colorGreen
          : colorsVariables.colorNegative;
      })
      .style("transform", `translate(0, 1rem)`);

    this.nodeArrowState
      .append("svg:image")
      .attr("xlink:href", (d) => {
        return d.growthDirection === "UP"
          ? require(`@/assets/images/charts/arrow.svg`)
          : require(`@/assets/images/charts/arrow_down.svg`);
      })

      .attr("width", ".75rem")
      .attr("height", ".75rem")
      .style("transform", "translate(-0.3rem, 0.6rem)");
  }
  static async getRedIdFromYedId(surveyId, router) {
    let surveysIds = store.getters["mainStats/getDerivedSurveys"];
    let yesSurvey = null;
    if (surveysIds) {
      yesSurvey = surveysIds.find((item) => item.redSurveyId == surveyId)
        .yesSurveyId;
    } else {
      await store.dispatch("mainStats/fetchDerivedSurveys");
      surveysIds = store.getters["mainStats/getDerivedSurveys"];
      yesSurvey = surveysIds.find((item) => item.redSurveyId == surveyId)
        .yesSurveyId;
    }
    router.push({
      name: "Experience",
      params: { id: yesSurvey },
    });
  }
}
